import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// ReactQueryDevtools desactivado: la UI flotante interfería con el layout.
// Para reactivar: descomentar este import + el render condicional al final
// del componente, y poner VITE_ENABLE_DEVTOOLS=true en .env.
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState, type ReactNode } from 'react';

import { me, useSesionStore } from '@/entities/usuario';
import { refresh } from '@/features/refresh-session';
import { useHidratarSesion } from '@/processes/auth';
import {
  isApiError,
  setAuthEndpointChecker,
  setForbiddenHandler,
  setLogoutHandler,
  setRefreshHandler,
  setTokenProvider,
} from '@/shared/api';
// import { getConfig } from '@/shared/config';
import { ErrorBoundary, ThemeProvider, Toaster, TooltipProvider } from '@/shared/ui';
import { AuthHydrationSplash } from '@/widgets/auth-hydration-splash';

/**
 * 401/403/404 son deterministas: reintentar duplica la llamada y alarga el
 * spinner sin cambiar el resultado (el 401 ya tiene su flujo de refresh en
 * el client, y el 403 dispara el forbidden handler). Solo se reintenta una
 * vez el resto (fallas transitorias de red/5xx).
 */
const NO_RETRY_STATUS = new Set([401, 403, 404]);

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && NO_RETRY_STATUS.has(error.statusCode)) return false;
  return failureCount < 1;
}

function buildQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
      mutations: { retry: 0 },
    },
  });
}

/**
 * Cableado de la inyeccion HTTP. Vive aqui (no en client.ts) para evitar
 * ciclos. Se ejecuta una sola vez al montar el provider raiz.
 */
function HttpDiSetup({ queryClient }: { queryClient: QueryClient }) {
  useEffect(() => {
    setTokenProvider(() => useSesionStore.getState().accessToken);

    setRefreshHandler(async () => {
      const { accessToken } = await refresh();
      useSesionStore.getState().setAccessToken(accessToken);
    });

    setLogoutHandler(() => {
      useSesionStore.getState().clear();
      queryClient.clear();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    });

    // Públicos sin sesión: auth/* + activación de cuenta. Su 401 (p. ej.
    // TOKEN_INVALID) no debe disparar refresh/logout (si no, se ve como
    // "Sin conexión" y saca al usuario de /activar). Ver di.ts.
    setAuthEndpointChecker((url) =>
      Boolean(url && /\/auth\/|\/users\/activate(?:[/?]|$)/.test(url)),
    );

    // 403 = el server rechazó por permisos/jerarquía. Refrescamos el
    // snapshot `SessionUser` desde /auth/me para que `canAccess` vuelva
    // a aplicar la regla correcta y oculte botones que ya no aplican
    // (caso típico: otro admin bajó nuestro rol en vivo).
    //
    // El cache SOLO se invalida si los permisos realmente cambiaron.
    // Invalidar siempre re-disparaba las mismas queries 403 en bucle
    // (403 → invalidateQueries → refetch → 403 → …) hasta chocar con el
    // rate limit del backend (429) y tumbar vistas sanas — un 403
    // legítimo y estable (permiso que el rol nunca tuvo) no debe tocar
    // el resto del cache. Cooldown además del flag `pending` para que
    // una ráfaga de 403s en paralelo no martille /auth/me.
    let pending = false;
    let lastRecheck = 0;
    const FORBIDDEN_RECHECK_COOLDOWN_MS = 30_000;
    setForbiddenHandler(() => {
      if (pending) return;
      if (!useSesionStore.getState().isAuthenticated) return;
      if (Date.now() - lastRecheck < FORBIDDEN_RECHECK_COOLDOWN_MS) return;
      pending = true;
      lastRecheck = Date.now();
      void me()
        .then((user) => {
          const estado = useSesionStore.getState();
          if (!estado.accessToken) return;
          const permisosCambiaron =
            JSON.stringify([...(estado.user?.permisos ?? [])].sort()) !==
              JSON.stringify([...user.permisos].sort()) || estado.user?.rol.id !== user.rol.id;
          estado.setSesion({ accessToken: estado.accessToken, user });
          if (permisosCambiaron) void queryClient.invalidateQueries();
        })
        .catch(() => {
          // Si /auth/me también falla, el flujo de 401 ya se ocupa.
        })
        .finally(() => {
          pending = false;
        });
    });
  }, [queryClient]);
  return null;
}

function HidratacionSesion() {
  useHidratarSesion();
  return null;
}

/**
 * Mientras `useHidratarSesion` resuelve (refresh cookie + GET /auth/me), no
 * pintamos el router para evitar el flash de `/login` cuando el usuario en
 * realidad sigue autenticado. El guard de `_private/route.tsx` queda como
 * segunda red de seguridad — esta es solo la UX inicial.
 */
function HydrationGate({ children }: { children: ReactNode }) {
  const isHydrated = useSesionStore((s) => s.isHydrated);
  if (!isHydrated) return <AuthHydrationSplash />;
  return <>{children}</>;
}

type AppProvidersProps = { children: ReactNode };

export function AppProviders({ children }: AppProvidersProps) {
  const [queryClient] = useState(buildQueryClient);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <HttpDiSetup queryClient={queryClient} />
          <HidratacionSesion />
          <TooltipProvider delayDuration={150}>
            <HydrationGate>{children}</HydrationGate>
            <Toaster />
          </TooltipProvider>
          {/* {enableDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null} */}
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
